import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
	MatAutocomplete,
	MatAutocompleteSelectedEvent,
	MatAutocompleteTrigger,
	MatOption,
} from '@angular/material/autocomplete';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { MatFormField, MatHint, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatButton } from '@angular/material/button';
import { CustomerType } from '@/reservation/reservation-summary/reservation-summary-owner/reservation-summary-owner-dialog/reservation-summary-owner-dialog.component';
import { FilteredOrganization, Organization, OwnerUser } from '@/reservation/reservation.model';
import { ReservationService } from '@/reservation/reservation.service';
import { InfoboxService } from '@/common/infobox/infobox.service';
import { LoaderService } from '@/common/loader/loader.service';
import {
	catchError,
	debounceTime,
	distinctUntilChanged,
	finalize,
	map,
	Observable,
	of,
	startWith,
	Subscription,
	switchMap,
	take,
} from 'rxjs';
import { MatDialogActions, MatDialogRef } from '@angular/material/dialog';
import { ReservationSummaryApiService } from '@/reservation/reservation-summary/services/reservation-summary-api.service';
import { createQueryParams } from '@/_utils/createQueryParams';

const MIN_SEARCH_LENGTH = 3;

enum SearchType {
	ORGANIZATION = 'organization',
	USER = 'user',
}

@Component({
	selector: 'app-owner-search',
	standalone: true,
	imports: [
		DatePipe,
		FormsModule,
		MatAutocomplete,
		MatRadioGroup,
		MatRadioButton,
		MatLabel,
		MatOption,
		MatFormField,
		ReactiveFormsModule,
		MatInput,
		MatAutocompleteTrigger,
		MatButton,
		MatHint,
		MatDialogActions,
	],
	templateUrl: './owner-search.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OwnerSearchComponent implements OnInit {
	readonly selectedCustomerType = signal(CustomerType.PRIVATE);
	readonly selectedUser = signal<OwnerUser>(null);
	readonly selectedOwnerOrganization = signal<Organization>(null);
	readonly filteredOrganizations = signal<FilteredOrganization[]>([]);
	readonly filteredUsers = signal<OwnerUser[]>([]);

	readonly customerType = CustomerType;

	readonly _service = inject(ReservationService);

	organizationSelect = new FormControl(this._service.organizations);
	usersSelect = new FormControl(this._service.users);
	readonly reservationId$ = this._service.reservation$.pipe(map((res) => res?.id));

	private readonly infobox = inject(InfoboxService);
	private readonly loader = inject(LoaderService);
	private readonly cdr = inject(ChangeDetectorRef);
	private readonly dialogRef = inject(MatDialogRef<OwnerSearchComponent>);
	private readonly apiService = inject(ReservationSummaryApiService);

	ngOnInit(): void {
		this._service.loadUsers();
		this.filteredUsers.set(this._service.users);
		this.filteredOrganizations.set(this._service.organizations);

		this.subscribeToFilter<FilteredOrganization>(
			this.organizationSelect,
			this._service.organizations,
			SearchType.ORGANIZATION,
			this.apiService.getOrganization.bind(this.apiService),
			(organizations: FilteredOrganization[]) => {
				this.filteredOrganizations.set(organizations);
			},
		);

		this.subscribeToFilter<OwnerUser>(
			this.usersSelect,
			this._service.users,
			SearchType.USER,
			this.apiService.getDataFull.bind(this.apiService),
			(users: OwnerUser[]) => {
				this.filteredUsers.set(users);
			},
		);
	}

	selectOrganization($event: MatAutocompleteSelectedEvent): void {
		this.selectedOwnerOrganization.set($event.option.value);
	}

	selectUser($event: MatAutocompleteSelectedEvent): void {
		this.selectedUser.set($event.option.value);
	}

	changeOwner(): void {
		const patchOrganizationPayload = {
			ownerUser: null,
			ownerOrganization: this.selectedOwnerOrganization()?.id,
		};

		const patchOwnerPayload = {
			ownerUser: this.selectedUser()?.id,
			ownerOrganization: null,
		};

		const payload = this.selectedUser() ? patchOwnerPayload : patchOrganizationPayload;

		this.loader.show('switch-owner');

		this.reservationId$.pipe(take(1)).subscribe((id) => {
			this.apiService
				.patchReservation(id, payload)
				.pipe(finalize(() => this.loader.hide('switch-owner')))
				.subscribe(() => {
					this.infobox.success('Pomyślnie zmieniono zgłaszającego');
					this._service.loadReservation(id);
				});
		});
	}

	changeSelectedData(isPrivateCustomer: boolean): void {
		this.filteredUsers.set(this._service.users);
		this.filteredOrganizations.set(this._service.organizations);

		if (isPrivateCustomer) {
			this.selectedOwnerOrganization.set(null);
		} else {
			this.selectedUser.set(null);
		}
	}

	displayFn(organization: FilteredOrganization | number): string {
		return organization && typeof organization === 'object' ? organization.name : '';
	}

	displayUsersFn(user: OwnerUser | OwnerUser[]): string {
		return Array.isArray(user) ? '' : `${user.firstName} ${user.lastName}`;
	}

	closeDialog(): void {
		this.dialogRef.close();
	}

	private subscribeToFilter<T>(
		control: FormControl,
		originalData: T[],
		searchType: SearchType,
		searchFn: (searchTerm: string) => Observable<T[]>,
		resultHandler: (data: T[]) => void,
	): Subscription {
		return control.valueChanges
			.pipe(
				startWith(null),
				debounceTime(400),
				distinctUntilChanged(),
				switchMap((value: T[] | string) =>
					this.filterSearch<T>(searchType, value || '', originalData, searchFn),
				),
			)
			.subscribe((data: T[]) => {
				resultHandler(data ?? originalData);
				this.cdr.detectChanges();
			});
	}

	private filterSearch<T>(
		searchType: SearchType,
		value: string | T[],
		originalData: T[],
		searchFn: (searchTerm: string) => Observable<T[]>,
	): Observable<T[]> {
		if (typeof value !== 'string') {
			return of(originalData);
		}

		const searchTerm = value.trim();

		if (searchTerm.length < MIN_SEARCH_LENGTH) {
			return of(originalData);
		}

		const params = searchType === SearchType.ORGANIZATION ? { name: searchTerm } : { full_name: searchTerm };

		return searchFn(createQueryParams(params)).pipe(
			catchError(() => {
				return of(originalData);
			}),
		);
	}
}
